<template>

    <div class="mt-6">
        <form>
            <ErrorSummary :response-status="responseStatus" />
            <div class="mt-6">
                <div class="divide-y divide-gray-200">
                    <div class="gap-6 mt-6 sm:grid sm:grid-cols-4">
                        <div class="sm:col-span-4">
                            <label class="block text-sm font-medium text-gray-700" for="cover-photo">
                                Upload Resume
                            </label>
                            <div
                                class="flex justify-center px-6 pt-5 pb-6 mt-1 border-2 border-dashed rounded-md border-clay-500">
                                <div v-if="resumes?.length >= 3" class="space-y-1 text-center">
                                    <p class="max-w-2xl text-sm text-clay-800">You currently have {{resumes.length}}
                                        resumes. You can only upload upto 3 resumes.</p>
                                </div>
                                <div v-else class="space-y-1 text-center">
                                    <Uploadcare :imagesOnly="false" input-accept-types="application/pdf"
                                        :validators="fileValidators" publicKey="c1ecae68d0087aa3986a"
                                        tabs="file url facebook gdrive dropbox instagram evernote flickr skydrive"
                                        @success="(response: any) => onUploadSuccess(response)">
                                        <svg class="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor"
                                            stroke-width="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                                stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                        <div class="flex text-sm text-gray-600">
                                            <a
                                                class="relative flex items-center justify-center px-8 py-2 text-sm font-medium text-white border border-transparent rounded-md bg-clay-800 hover:bg-clay-950">Upload
                                                File</a>
                                        </div>
                                        <p class="text-xs text-clay-800">
                                            PDF up to 2MB
                                        </p>
                                    </Uploadcare>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <p class="mt-4 text-sm text-clay-800">* Be careful including personal information on your resume such as
                    your phone number and email address. Your PDF resume will be available for viewing by users on the
                    internet. Anyone can view your resume and contact you if you include an email address or phone
                    number. StageAgent has no association with anyone who might contact you and is not responsible for
                    your interactions outside of our website.</p>
            </div>
            <div class="bg-white">
                <div class="py-8 mx-auto sm:py-12">
                    <div class="flex items-center justify-between space-x-4">
                        <h2 class="text-lg font-medium text-gray-900">Resumes / CVs</h2>
                    </div>
                    <div class="grid grid-cols-1 mt-6 gap-x-8 gap-y-8 sm:grid-cols-2 sm:gap-y-10 lg:grid-cols-4">

                        <SkeletonCard class="mt-4 col-span-full" v-if="isFetching" card-style="long" :num-of-cards=1 />

                        <div v-else class="sm:col-span-4">
                            <div v-if="resumes?.length < 1">

                                You haven't uploaded a PDF of your resume. Upload your resume to gain exposure to more
                                opportunities!

                            </div>
                            <div v-else class="text-sm font-medium text-clay-800"> Uploads</div>
                            <div v-for="resume in resumes" :key="resume.id" class="mt-1 text-sm text-gray-900">
                                <ul class="border border-gray-200 divide-y divide-gray-200 rounded-md" role="list">

                                    <li class="z-40" x-cloak x-data="dropdown">
                                        <div class="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                                            <div class="flex items-center flex-1 w-0">
                                                <!-- Heroicon name: solid/paper-clip -->
                                                <svg aria-hidden="true" class="w-5 h-5 text-gray-400 shrink-0"
                                                    fill="currentColor" viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                    <path clip-rule="evenodd"
                                                        d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                                                        fill-rule="evenodd" />
                                                </svg>
                                                <span class="flex-1 w-0 ml-2 truncate"> {{
                                                    resume.friendlyName
                                                    }} </span>
                                            </div>
                                            <div class="ml-4 shrink-0" v-if="resumeBeingDeleted !== resume.id">
                                                <button
                                                    class="inline-block pl-3 font-medium text-powder-600 hover:text-powder-500"
                                                    @click.prevent="deleteResume(resume)">
                                                    Delete
                                                </button>
                                                <a :href="resume.url"
                                                    class="inline-block pl-3 font-medium text-powder-600 hover:text-powder-500"
                                                    target="_blank" alt="download resume" title="download resume">
                                                    <svg class="w-5 h-5" fill="none" stroke="currentColor"
                                                        stroke-width="1.5" viewBox="0 0 24 24"
                                                        xmlns="http://www.w3.org/2000/svg">
                                                        <path
                                                            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                                                            stroke-linecap="round" stroke-linejoin="round" />
                                                    </svg>
                                                </a>
                                                <button aria-controls="resumeName" aria-expanded="true"
                                                    class="inline-block pl-3 font-medium text-powder-600 hover:text-powder-500"
                                                    type="button" x-on:click="toggle" alt="Edit" title="Edit">
                                                    <svg class="w-5 h-5" fill="none" stroke="currentColor"
                                                        stroke-width="1.5" viewBox="0 0 24 24"
                                                        xmlns="http://www.w3.org/2000/svg">
                                                        <path
                                                            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125"
                                                            stroke-linecap="round" stroke-linejoin="round" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <div v-else class="ml-4 shrink-0">
                                                <p class="text-sa-red-600">Deleting...please wait.</p>
                                            </div>
                                        </div>

                                        <div id="resumeName" aria-labelledby="menu-button" aria-orientation="vertical"
                                            class="flex items-center justify-between py-3 pl-3 pr-4 text-sm" role="menu"
                                            tabindex="-1" x-show="open">
                                            <div class="">
                                                <label
                                                    class="justify-center inline-block px-3 font-medium text-gray-700 ext-sm algin-middle"
                                                    for="">Resume
                                                    Title</label>
                                                <input v-model="resume.friendlyName" :placeholder="resume.friendlyName"
                                                    class="inline-block px-3 py-2 mt-1 border rounded-md shadow-sm border-clay-500 focus:outline-hidden focus:ring-clay-800 focus:border-clay-800 sm:text-sm "
                                                    type="text">
                                            </div>
                                            <div class="ml-4 shrink-0">
                                                <button class="pl-3 font-medium text-powder-600 hover:text-powder-500"
                                                    @click.prevent="editResume(resume)">Save
                                                </button>
                                            </div>

                                        </div>

                                    </li>
                                </ul>
                            </div>
                            <p v-if="isUploading" class="mt-4 text-sa-red-600">Uploading...please wait.</p>
                        </div>


                    </div>
                </div>
            </div>
            <div class="mt-6">
                <div class="p-6 text-center rounded-lg bg-powder-50">
                    <p>Need help properly formatting your resume? <a href="/pages/templates"
                            class="underline hover:font-semibold">Download a Template</a> </p>

                </div>
            </div>
        </form>
    </div>

    <NotificationSuccess :show="updateSuccessful" content="Your changes were successfully saved."
        title="Successfully saved!" />

</template>

<script lang="ts" setup>
import {onMounted, Ref, ref} from "vue";
import {client, validateFileSize, validateFileType} from '@/shared';
import {
    DeleteResume,
    GetResumes,
    GetResumesResponse,
    UpdateFriendlyNameForUploadedResume,
    UploadedResumeDTO,
    UploadResume
} from "@/dtos";
// @ts-ignore
import Uploadcare from 'uploadcare-vue/src/Uploadcare.vue';
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import NotificationSuccess from "@/main/components/NotificationSuccess.vue";
import SkeletonCard from "@/main/components/skeletons/SkeletonCard.vue";
import {trackEvent} from '@/shared';

const responseStatus = ref(null);
const resumes: Ref<UploadedResumeDTO[]> = ref([]);

const updateSuccessful: Ref<Boolean> = ref(false);


const isFetching = ref(true);
onMounted(async () => {
    await getResumes();
});

const getResumes = async () => {
    await client.get<GetResumesResponse>(new GetResumes())
        .then(response => {
            resumes.value = response.resumes;
            isFetching.value = false;
        });
}

const fileValidators = [
    validateFileType('pdf'),
    validateFileSize(1024*1024*4) // 4mb
];


const isUploading = ref(false);
const onUploadSuccess = async (response: any) => {
    console.log(response);
    isUploading.value = true;
    await client.post<GetResumesResponse>(new UploadResume({
        fileUrl: response.cdnUrl,
        friendlyName: response.name
    }))
        .then(async (res) => {
            updateSuccessful.value = true;
            responseStatus.value = null;
            resumes.value = res.resumes;

            trackEvent( 'profile_updated', {
                method: 'resume_uploaded'
            });
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });

    isUploading.value = false;
}

const editResume = async (resume: UploadedResumeDTO) => {
    await client.post<GetResumesResponse>(new UpdateFriendlyNameForUploadedResume({
        id: resume.id,
        friendlyName: resume.friendlyName
    }))
        .then(async (res) => {
            updateSuccessful.value = true;
            responseStatus.value = null;
            resumes.value = res.resumes;
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
}

const resumeBeingDeleted = ref('');
const deleteResume = async (resume: UploadedResumeDTO) => {
    resumeBeingDeleted.value = resume.id;
    await client.delete<GetResumesResponse>(new DeleteResume({
        id: resume.id
    }))
        .then(async (res) => {
            updateSuccessful.value = true;
            responseStatus.value = null;
            resumes.value = res.resumes;
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
    resumeBeingDeleted.value = '';
}


</script>
